import { get, post, fetchStream } from "./client";

export interface AssistantContext {
  currentPage?: string;
  activeTab?: string;
  selectedItems?: string[];
}

export interface SendMessageRequest {
  message: string;
  conversation_id?: string | null;
  context?: AssistantContext;
}

export interface ToolCall {
  name: string;
  arguments?: Record<string, unknown>;
  result?: unknown;
}

export interface ClearConversationResponse {
  success: boolean;
}

export interface ConversationHistoryMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  tool_calls?: Array<{ name: string }>;
}

export interface ConversationHistoryResponse {
  conversation_id: string;
  messages: ConversationHistoryMessage[];
}

export interface AssistantMemoryStatsResponse {
  team_id: string;
  conversation_count: number;
  active_conversation_count: number;
  cleared_conversation_count: number;
  web_conversation_count: number;
  slack_conversation_count: number;
  message_count: number;
  avg_messages_per_conversation: number;
  oldest_message_at: string | null;
  latest_message_at: string | null;
}

export type StreamEvent =
  | { type: "content"; delta: string }
  | { type: "content"; text: string }
  | { type: "tool_call"; name: string; arguments: Record<string, unknown> }
  | { type: "tool_result"; name: string; result: unknown }
  | {
      type: "harness_status";
      status: string;
      run_id?: string;
      message?: string;
      permission_profile?: string;
      model?: string;
    }
  | {
      type: "harness_tool_call";
      name: string;
      arguments: Record<string, unknown>;
    }
  | {
      type: "harness_run_done";
      run_id: string;
      selected_skills: string[];
      tool_calls: ToolCall[];
    }
  | {
      type: "harness_approval_required";
      approval_id: string;
      run_id: string;
      tool_name: string;
      reason: string;
      arguments: Record<string, unknown>;
    }
  | { type: "done"; conversation_id: string; tool_calls: ToolCall[] }
  | { type: "error"; message: string };

export interface HarnessApprovalResponse {
  success: boolean;
  approval_id: string;
  message: string;
  result?: unknown;
}

export async function streamMessage(
  teamId: string,
  request: SendMessageRequest,
  onChunk: (event: StreamEvent) => void,
): Promise<void> {
  const response = await fetchStream(
    `/api/teams/${teamId}/assistant/chat/stream`,
    request,
  );

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Response body is not readable");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    let isReading = true;
    while (isReading) {
      const { done, value } = await reader.read();
      if (done) {
        isReading = false;
        continue;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // Keep the last (potentially incomplete) line in buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const payload = trimmed.startsWith("data:")
          ? trimmed.slice(5).trim()
          : trimmed;
        if (!payload) continue;

        try {
          const event: StreamEvent = JSON.parse(payload);
          onChunk(event);
        } catch {
          // Skip malformed lines
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      try {
        const payload = buffer.trim().startsWith("data:")
          ? buffer.trim().slice(5).trim()
          : buffer.trim();
        if (!payload) {
          return;
        }
        const event: StreamEvent = JSON.parse(payload);
        onChunk(event);
      } catch {
        // Skip malformed final line
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function clearConversation(
  teamId: string,
  conversationId: string,
): Promise<ClearConversationResponse> {
  return post<ClearConversationResponse>(
    `/api/teams/${teamId}/assistant/clear`,
    {
      conversation_id: conversationId,
    },
  );
}

export async function getConversationHistory(
  teamId: string,
  conversationId: string,
): Promise<ConversationHistoryResponse> {
  return get<ConversationHistoryResponse>(
    `/api/teams/${teamId}/assistant/conversation/${conversationId}`,
  );
}

export async function approveHarnessAction(
  teamId: string,
  approvalId: string,
): Promise<HarnessApprovalResponse> {
  return post<HarnessApprovalResponse>(
    `/api/teams/${teamId}/assistant/harness/approvals/${approvalId}`,
    {},
  );
}

export async function getAssistantMemoryStats(
  teamId: string,
): Promise<AssistantMemoryStatsResponse> {
  return get<AssistantMemoryStatsResponse>(
    `/api/teams/${teamId}/assistant/memory/stats`,
  );
}
